import React, {useState} from 'react'
import {FormattedNumber, useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {NoStyleButton} from '@vio/atlas/helpers/NoStyleButton'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'
import {
  pointsProgram,
  toEarnedPoints
} from '@vio/core/src/loyalty/pointsProgram'

import {usePointsPriceDisplay} from '../hooks/usePointsPriceDisplay'
import {loyaltyMessages} from '../loyaltyMessages'

const Row = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s100};
`

const InfoSlot = styled.span`
  display: flex;
`

const InfoButton = styled(NoStyleButton)`
  display: flex;
`

interface Props {
  /** Stay total incl. taxes in the display currency */
  amount: number
}

export const EarnPoints = ({amount}: Props) => {
  const {formatMessage} = useIntl()
  const {isDesktop} = useDeviceLayout()
  const {canEarn, currencyCode} = usePointsPriceDisplay()
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const points = canEarn ? toEarnedPoints(amount, currencyCode) : undefined
  if (!points) return null

  const title = formatMessage(loyaltyMessages.earnPointsInfoTitle)
  const content = formatMessage(loyaltyMessages.earnPointsInfoContent, {
    programName: pointsProgram.programName
  })

  const infoButton = (
    <InfoButton
      type="button"
      aria-label={title}
      onClick={isDesktop ? undefined : () => setIsSheetOpen(true)}
    >
      <Icon name="Information" size="sm" colorPath="content.brand.primary" />
    </InfoButton>
  )

  return (
    <Row>
      <Text variant="labelXS" tag="span" colorPath="content.brand.primary">
        {formatMessage(loyaltyMessages.earnPoints, {
          count: points,
          points: <FormattedNumber value={points} />
        })}
      </Text>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- stops info clicks reaching the clickable offer card; portal events bubble through the React tree */}
      <InfoSlot
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        {isDesktop ? (
          <Tooltip
            placement={TooltipPlacement.BottomEnd}
            title={title}
            content={content}
          >
            {infoButton}
          </Tooltip>
        ) : (
          <>
            {infoButton}
            <BottomSheet
              isOpen={isSheetOpen}
              onClose={() => setIsSheetOpen(false)}
              navBarProps={{centerContent: title}}
            >
              <Text variant="bodyM">{content}</Text>
            </BottomSheet>
          </>
        )}
      </InfoSlot>
    </Row>
  )
}
